import React, { useEffect, useState } from 'react';
import { Calendar, Clock, X, ChevronDown, ChevronUp } from 'lucide-react';
import { updateOrderWithAudit } from '../utils/orderAudit.js';

const formatDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDate = (dateString) => {
  if (!dateString) return 'No Schedule';
  return new Date(dateString).toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const formatDateTime = (dateString) => {
  if (!dateString) return '---';
  return new Date(dateString).toLocaleString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const getScheduleOptionForDate = (dateString) => {
  if (!dateString) return 'no_schedule';
  if (dateString === formatDateInputValue(new Date())) return 'today';

  const tomorrow = new Date();
  tomorrow.setHours(0, 0, 0, 0);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (dateString === formatDateInputValue(tomorrow)) return 'tomorrow';

  const inTwoDays = new Date();
  inTwoDays.setHours(0, 0, 0, 0);
  inTwoDays.setDate(inTwoDays.getDate() + 2);
  if (dateString === formatDateInputValue(inTwoDays)) return 'in_2_days';

  return 'custom';
};

const getPresetScheduleDisplayDate = (optionId) => {
  const baseDate = new Date();
  baseDate.setHours(0, 0, 0, 0);

  if (optionId === 'tomorrow') baseDate.setDate(baseDate.getDate() + 1);
  if (optionId === 'in_2_days') baseDate.setDate(baseDate.getDate() + 2);

  return formatDate(formatDateInputValue(baseDate));
};

const buildScheduleHistoryEntry = ({ action, date, note, remarks, timestamp, user }) => ({
  action,
  date: date || '',
  note: note || '',
  remarks: remarks || '',
  updatedAt: timestamp,
  updatedBy: user?.email || user?.name || 'Unknown'
});

const detailCardStyle = {
  border: '1px solid #e5e7eb',
  borderRadius: '10px',
  backgroundColor: '#f8fafc',
  padding: '14px 16px'
};

const DispatchScheduleDrawer = ({
  isOpen,
  onClose,
  order,
  db,
  user,
  isEditable = false,
  onOrderUpdated
}) => {
  const [scheduleDateOption, setScheduleDateOption] = useState('today');
  const [customScheduleDate, setCustomScheduleDate] = useState('');
  const [scheduleNote, setScheduleNote] = useState('');
  const [scheduleRemarks, setScheduleRemarks] = useState('');
  const [isSavingSchedule, setIsSavingSchedule] = useState(false);
  const [isScheduleHistoryOpen, setIsScheduleHistoryOpen] = useState(false);

  useEffect(() => {
    if (!isOpen || !order) return;

    const todayValue = formatDateInputValue(new Date());
    setScheduleDateOption(getScheduleOptionForDate(order.dispatchScheduleDate));
    setCustomScheduleDate(order.dispatchScheduleDate || todayValue);
    setScheduleNote(order.dispatchScheduleNote || 'Tentative');
    setScheduleRemarks(order.dispatchScheduleRemarks || '');
    setIsScheduleHistoryOpen(false);
  }, [isOpen, order]);

  useEffect(() => {
    if (scheduleDateOption === 'no_schedule') {
      setScheduleNote('');
      setScheduleRemarks('');
    }
  }, [scheduleDateOption]);

  if (!isOpen || !order) return null;

  const getScheduleDateFromOption = () => {
    const baseDate = new Date();
    baseDate.setHours(0, 0, 0, 0);

    if (scheduleDateOption === 'no_schedule') return '';
    if (scheduleDateOption === 'today') return formatDateInputValue(baseDate);
    if (scheduleDateOption === 'tomorrow') {
      const tomorrow = new Date(baseDate);
      tomorrow.setDate(tomorrow.getDate() + 1);
      return formatDateInputValue(tomorrow);
    }
    if (scheduleDateOption === 'in_2_days') {
      const inTwoDays = new Date(baseDate);
      inTwoDays.setDate(inTwoDays.getDate() + 2);
      return formatDateInputValue(inTwoDays);
    }
    return customScheduleDate;
  };

  const handleSave = async () => {
    if (!isEditable || !db || !order?.id) return;

    const selectedDate = getScheduleDateFromOption();
    const isNoSchedule = scheduleDateOption === 'no_schedule';

    if (!isNoSchedule && !scheduleNote) {
      alert("Please choose a schedule note.");
      return;
    }

    if (isNoSchedule && !scheduleRemarks.trim()) {
      alert("Please add Schedule Remarks before saving No Schedule.");
      return;
    }

    setIsSavingSchedule(true);
    try {
      const timestamp = new Date().toISOString();
      const nextScheduleNote = isNoSchedule ? '' : scheduleNote;
      const nextScheduleRemarks = scheduleRemarks.trim();
      const nextHistory = [
        ...(order.dispatchScheduleHistory || []),
        buildScheduleHistoryEntry({
          action: isNoSchedule ? 'reset' : 'saved',
          date: selectedDate,
          note: nextScheduleNote,
          remarks: nextScheduleRemarks,
          timestamp,
          user
        })
      ];

      const payload = {
        dispatchScheduleDate: selectedDate || '',
        dispatchScheduleNote: nextScheduleNote,
        dispatchScheduleRemarks: nextScheduleRemarks,
        dispatchScheduleHistory: nextHistory,
        dispatchScheduleUpdatedAt: timestamp,
        updatedAt: timestamp
      };

      if (!order.dispatchScheduledAt) payload.dispatchScheduledAt = timestamp;
      if (user?.email) payload.dispatchScheduledBy = user.email;

      await updateOrderWithAudit(db, {
        orderId: order.id,
        orderPayload: {
          ...payload,
          updatedBy: user?.displayName || user?.email || 'Unknown',
          updatedByEmail: user?.email || ''
        },
        previousData: order,
        user,
        historyEvent: {
          action: 'schedule_set',
          status: order.status,
          user,
          timestamp,
          remarks: nextScheduleRemarks
        },
        auditFields: {
          scheduleSetAt: timestamp,
          scheduleSetBy: user?.displayName || user?.email || 'Unknown'
        }
      });
      onOrderUpdated?.({ ...order, ...payload });
      onClose?.();
    } catch (error) {
      alert("Failed to save dispatch schedule.");
    } finally {
      setIsSavingSchedule(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.35)',
        zIndex: 9998
      }}
      onClick={onClose}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '420px',
          maxWidth: '100%',
          height: '100%',
          backgroundColor: '#ffffff',
          boxShadow: '-12px 0 28px rgba(15, 23, 42, 0.18)',
          padding: '24px 24px 120px 24px',
          overflowY: 'auto',
          boxSizing: 'border-box'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <h3 style={{ margin: 0, color: '#111827' }}>Dispatch Schedule</h3>
            <p style={{ margin: '6px 0 0 0', fontSize: '13px', color: '#6b7280' }}>
              #{order.orderNumber || order.id}
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
            <X size={20} />
          </button>
        </div>

        {isEditable ? (
          <>
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#374151', marginBottom: '12px' }}>
                Schedule Date
              </label>
              <div style={{ display: 'grid', gap: '10px' }}>
                {[
                  { id: 'today', label: 'Today' },
                  { id: 'tomorrow', label: 'Tomorrow' },
                  { id: 'in_2_days', label: 'In 2 Days' },
                  { id: 'custom', label: 'Custom' }
                ].map((option) => (
                  <label
                    key={option.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '12px',
                      padding: '12px 14px',
                      border: scheduleDateOption === option.id ? '1px solid #60a5fa' : '1px solid #d1d5db',
                      borderRadius: '10px',
                      backgroundColor: scheduleDateOption === option.id ? '#eff6ff' : '#ffffff',
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <input type="radio" name="scheduleDateOption" value={option.id} checked={scheduleDateOption === option.id} onChange={(e) => setScheduleDateOption(e.target.value)} />
                      <span style={{ fontSize: '14px', color: '#111827', fontWeight: '600' }}>{option.label}</span>
                    </div>
                    {option.id !== 'custom' && (
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>
                        {getPresetScheduleDisplayDate(option.id)}
                      </span>
                    )}
                  </label>
                ))}
              </div>

              {scheduleDateOption === 'custom' && (
                <div style={{ marginTop: '12px' }}>
                  <input
                    type="date"
                    value={customScheduleDate}
                    onChange={(e) => setCustomScheduleDate(e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>
              )}

              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '12px',
                  padding: '12px 14px',
                  border: scheduleDateOption === 'no_schedule' ? '1px solid #fca5a5' : '1px solid #fecaca',
                  borderRadius: '10px',
                  backgroundColor: scheduleDateOption === 'no_schedule' ? '#fee2e2' : '#fef2f2',
                  cursor: 'pointer',
                  marginTop: '10px'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input type="radio" name="scheduleDateOption" value="no_schedule" checked={scheduleDateOption === 'no_schedule'} onChange={(e) => setScheduleDateOption(e.target.value)} />
                  <span style={{ fontSize: '14px', color: '#991b1b', fontWeight: '600' }}>No Schedule</span>
                </div>
              </label>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#374151', marginBottom: '10px' }}>
                Schedule Note
              </label>
              <select
                value={scheduleNote}
                onChange={(e) => setScheduleNote(e.target.value)}
                disabled={scheduleDateOption === 'no_schedule'}
                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px', backgroundColor: scheduleDateOption === 'no_schedule' ? '#f9fafb' : '#fff' }}
              >
                <option value="">Select note...</option>
                <option value="Stock expected">Stock expected</option>
                <option value="Tentative">Tentative</option>
                <option value="Client requested">Client requested</option>
                <option value="Transport planning">Transport planning</option>
                <option value="Material under preparation">Material under preparation</option>
              </select>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#374151', marginBottom: '10px' }}>
                Schedule Remarks
              </label>
              <textarea
                value={scheduleRemarks}
                onChange={(e) => setScheduleRemarks(e.target.value)}
                placeholder="Add any schedule context for ops and sales..."
                style={{ width: '100%', minHeight: '88px', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px', backgroundColor: '#fff', resize: 'vertical', boxSizing: 'border-box' }}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={onClose} style={{ padding: '10px 16px', borderRadius: '8px', border: '1px solid #d1d5db', backgroundColor: '#fff', color: '#374151', cursor: 'pointer', fontWeight: '600' }}>
                Cancel
              </button>
              <button onClick={handleSave} disabled={isSavingSchedule} style={{ padding: '10px 16px', borderRadius: '8px', border: 'none', backgroundColor: '#2563eb', color: '#fff', cursor: isSavingSchedule ? 'not-allowed' : 'pointer', fontWeight: '700', opacity: isSavingSchedule ? 0.7 : 1 }}>
                {isSavingSchedule ? 'Saving...' : 'Save'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div
              style={{
                marginBottom: '24px',
                padding: '18px',
                borderRadius: '16px',
                background: order.dispatchScheduleDate
                  ? 'linear-gradient(135deg, #eff6ff 0%, #f8fafc 100%)'
                  : 'linear-gradient(135deg, #fef2f2 0%, #fff7ed 100%)',
                border: order.dispatchScheduleDate ? '1px solid #bfdbfe' : '1px solid #fecaca'
              }}
            >
              <div style={{ fontSize: '11px', fontWeight: '800', letterSpacing: '0.12em', color: '#64748b', textTransform: 'uppercase', marginBottom: '10px' }}>
                Dispatch Schedule
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                <div
                  style={{
                    width: '42px',
                    height: '42px',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: order.dispatchScheduleDate ? '#dbeafe' : '#fee2e2'
                  }}
                >
                  {order.dispatchScheduleDate ? <Calendar size={20} color="#2563eb" /> : <Clock size={20} color="#dc2626" />}
                </div>
                <div>
                  <div style={{ fontSize: '26px', lineHeight: 1.1, fontWeight: '800', color: '#0f172a' }}>
                    {formatDate(order.dispatchScheduleDate)}
                  </div>
                  <div style={{ marginTop: '4px', fontSize: '13px', color: '#64748b', fontWeight: '600' }}>
                    {order.dispatchScheduleDate ? 'Tentative dispatch commitment from ops' : 'No dispatch date has been scheduled yet'}
                  </div>
                </div>
              </div>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '7px 12px',
                  borderRadius: '999px',
                  backgroundColor: order.dispatchScheduleNote ? '#ffffff' : '#fff7ed',
                  border: order.dispatchScheduleNote ? '1px solid #e5e7eb' : '1px solid #fed7aa',
                  color: order.dispatchScheduleNote ? '#334155' : '#9a3412',
                  fontSize: '13px',
                  fontWeight: '700'
                }}
              >
                <span>{order.dispatchScheduleNote || 'Awaiting schedule note'}</span>
              </div>
            </div>

            <div style={{ display: 'grid', gap: '14px', marginBottom: '24px' }}>
              <div style={detailCardStyle}>
                <div style={{ fontSize: '11px', fontWeight: '800', letterSpacing: '0.1em', color: '#64748b', marginBottom: '8px', textTransform: 'uppercase' }}>
                  Schedule Remarks
                </div>
                <div style={{ fontSize: '15px', lineHeight: 1.6, color: '#0f172a', whiteSpace: 'pre-wrap' }}>
                  {order.dispatchScheduleRemarks || 'No additional remarks shared by ops.'}
                </div>
              </div>

              <div style={{ ...detailCardStyle, display: 'grid', gap: '10px' }}>
                <div>
                  <div style={{ fontSize: '11px', fontWeight: '800', letterSpacing: '0.1em', color: '#64748b', marginBottom: '4px', textTransform: 'uppercase' }}>
                    Last Updated
                  </div>
                  <div style={{ fontSize: '15px', fontWeight: '700', color: '#111827' }}>
                    {formatDateTime(order.dispatchScheduleUpdatedAt)}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', fontWeight: '800', letterSpacing: '0.1em', color: '#64748b', marginBottom: '4px', textTransform: 'uppercase' }}>
                    Updated By
                  </div>
                  <div style={{ fontSize: '14px', color: '#334155', fontWeight: '600' }}>
                    {order.dispatchScheduledBy || '---'}
                  </div>
                </div>
              </div>
            </div>

          </>
        )}

        <div style={{ marginTop: '24px' }}>
          <button
            type="button"
            onClick={() => setIsScheduleHistoryOpen(prev => !prev)}
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderRadius: '10px', border: '1px solid #e5e7eb', backgroundColor: '#f8fafc', cursor: 'pointer', color: '#111827', fontSize: '13px', fontWeight: '700' }}
          >
            <span>Schedule History</span>
            <span style={{ color: '#6b7280', fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              {isScheduleHistoryOpen ? 'Hide' : 'Show'}
              {isScheduleHistoryOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </span>
          </button>
          {isScheduleHistoryOpen && (
            <div style={{ marginTop: '10px', border: '1px solid #e5e7eb', borderRadius: '10px', backgroundColor: '#f8fafc', overflow: 'hidden' }}>
              {(order.dispatchScheduleHistory || []).length === 0 ? (
                <div style={{ padding: '14px 16px', fontSize: '13px', color: '#6b7280' }}>No schedule history yet.</div>
              ) : (
                [...(order.dispatchScheduleHistory || [])].slice().reverse().map((entry, index) => (
                  <div key={`${entry.updatedAt || 'history'}-${index}`} style={{ padding: '14px 16px', borderTop: index === 0 ? 'none' : '1px solid #e5e7eb' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginBottom: '6px' }}>
                      <span style={{ fontSize: '13px', fontWeight: '700', color: entry.action === 'reset' ? '#dc2626' : '#1d4ed8' }}>
                        {entry.action === 'reset' ? 'Reset to No Schedule' : 'Schedule Saved'}
                      </span>
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>{formatDateTime(entry.updatedAt)}</span>
                    </div>
                    <div style={{ fontSize: '13px', color: '#374151', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <span>Date: {entry.date ? formatDate(entry.date) : 'No Schedule'}</span>
                      <span>Note: {entry.note || '---'}</span>
                      <span>Remarks: {entry.remarks || '---'}</span>
                      <span>By: {entry.updatedBy || 'Unknown'}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DispatchScheduleDrawer;
